/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Search, X, Check } from 'lucide-react';

const MultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select options...",
  label = "Permissions",
  searchPlaceholder = "Search..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.key?.toLowerCase().includes(search.toLowerCase()) ||
    opt.module?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleOption = (id) => {
    const newSelected = selected.includes(id)
      ? selected.filter(item => item !== id)
      : [...selected, id];
    onChange(newSelected);
  };

  const removeSelected = (e, id) => {
    e.stopPropagation();
    onChange(selected.filter(item => item !== id));
  };

  return (
    <div className={`space-y-2 relative ${isOpen ? 'z-50' : ''}`} ref={containerRef}>
      <label className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
        {label} ({selected.length})
      </label>

      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`min-h-[54px] w-full px-4 py-2 border rounded-2xl cursor-pointer transition-all flex items-center justify-between gap-2 ${isOpen ? 'ring-4 ring-primary/10 border-primary bg-base-100' : 'border-base-300 hover:border-base-content/20'}`}
      >
        <div className="flex flex-wrap gap-1.5 flex-1">
          {selected.length === 0 ? (
            <span className="text-base-content/20 font-bold text-sm">{placeholder}</span>
          ) : (
            selected.map(id => {
              const opt = options.find(o => (o.id || o.key) === id);
              return (
                <span
                  key={id}
                  className="bg-primary text-primary-content text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-lg flex items-center gap-1 group/tag animate-in zoom-in-95 duration-200"
                >
                  {opt?.key || id}
                  <X
                    size={12}
                    className="cursor-pointer hover:text-white transition-colors"
                    onClick={(e) => removeSelected(e, id)}
                  />
                </span>
              );
            })
          )}
        </div>
        <ChevronDown size={18} className={`text-base-content/20 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 w-full mt-2 bg-base-100 border border-base-300 rounded-2xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-3 border-b border-base-300 bg-base-200/30">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30" />
              <input
                autoFocus
                type="text"
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-4 py-2 bg-base-100 border border-base-300 rounded-xl text-xs font-bold outline-none focus:border-primary transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-[300px] overflow-y-auto p-2 space-y-4">
            {/* Group by Module (only when options actually specify one) */}
            {(() => {
              const hasModules = filteredOptions.some(opt => opt.module);
              return Object.entries(
                filteredOptions.reduce((acc, opt) => {
                  const mod = opt.module || 'General';
                  if (!acc[mod]) acc[mod] = [];
                  acc[mod].push(opt);
                  return acc;
                }, {})
              ).map(([module, opts]) => (
                <div key={module} className="space-y-1">
                  {hasModules && (
                    <div className="px-3 py-1 text-[9px] font-black text-base-content/30 uppercase tracking-[0.2em] bg-base-200/50 rounded-lg">
                      {module}
                    </div>
                  )}
                  {opts.map(opt => {
                    const id = opt.id || opt.key;
                    const isActive = selected.includes(id);
                    return (
                      <div
                        key={id}
                        onClick={(e) => { e.stopPropagation(); toggleOption(id); }}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all group ${isActive ? 'bg-primary/10 text-primary' : 'hover:bg-base-200 text-base-content/60'}`}
                      >
                        <div className="flex flex-col">
                          <span className="text-xs font-black uppercase tracking-tight">{opt.key}</span>
                          {opt.description && <span className="text-[10px] opacity-40 font-bold">{opt.description}</span>}
                        </div>
                        {isActive && <Check size={14} className="animate-in zoom-in-50 duration-200" />}
                      </div>
                    );
                  })}
                </div>
              ));
            })()}

            {filteredOptions.length === 0 && (
              <div className="py-8 text-center text-[10px] font-black text-base-content/20 uppercase tracking-widest">
                No matches found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      key: PropTypes.string,
      module: PropTypes.string,
      description: PropTypes.string,
    })
  ),
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  searchPlaceholder: PropTypes.string,
};
